import { extractApiErrorMessage } from '../../../utils/errorHandler';
import { useState, useCallback } from 'react';
import { message } from 'antd';
import { r5d6Api, PendingItem, PendingResponse, ReverseResponse, ReversalFilter } from '../../../services/r5d6Api';

export interface UseR5D6ReversalReturn {
  // State
  loading: boolean;
  reversing: boolean;
  pendingItems: PendingItem[];
  total: number;
  selectedKeys: string[];
  filter: ReversalFilter;

  // Actions
  setFilter: (f: ReversalFilter) => void;
  setSelectedKeys: (keys: string[]) => void;
  fetchPending: () => Promise<void>;
  batchReverse: () => Promise<ReverseResponse | null>;
}

export function useR5D6Reversal(api = r5d6Api): UseR5D6ReversalReturn {
  const [loading, setLoading] = useState(false);
  const [reversing, setReversing] = useState(false);
  const [pendingItems, setPendingItems] = useState<PendingItem[]>([]);
  const [total, setTotal] = useState(0);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [filter, setFilter] = useState<ReversalFilter>({ docStart: '', docEnd: '' });

  const fetchPending = useCallback(async () => {
    if (!filter.docStart || !filter.docEnd) {
      message.warning('請輸入單號起始和結束');
      return;
    }
    setLoading(true);
    setSelectedKeys([]);
    try {
      const resp: PendingResponse = await api.getPending(filter);
      const items = resp.items || [];
      setPendingItems(items);
      setTotal(resp.total || 0);
      // Pre-select items where default_checked=true (i.e. status='P')
      const defaultSelected = items
        .filter((item) => item.default_checked)
        .map((item) => item.doc_no);
      setSelectedKeys(defaultSelected);
    } catch (err: unknown) {
      const msg = extractApiErrorMessage(err, '查詢失敗');
      message.error(msg);
    } finally {
      setLoading(false);
    }
  }, [api, filter]);

  const batchReverse = useCallback(async (): Promise<ReverseResponse | null> => {
    if (selectedKeys.length === 0) {
      message.warning('請至少勾選一筆退出單進行沖銷');
      return null;
    }
    setReversing(true);
    try {
      const resp = await api.reverse(selectedKeys);
      message.success(resp.message);
      // Refresh list after reversal
      await fetchPending();
      return resp;
    } catch (err: unknown) {
      const msg = extractApiErrorMessage(err, '沖銷失敗');
      message.error(msg);
      return null;
    } finally {
      setReversing(false);
    }
  }, [api, selectedKeys, fetchPending]);

  return {
    loading,
    reversing,
    pendingItems,
    total,
    selectedKeys,
    filter,
    setFilter,
    setSelectedKeys,
    fetchPending,
    batchReverse,
  };
}
